/**
 * Plain-text report for "Copy diagnostic report". Written to be pasted into a
 * support ticket: scope, every finding with its steps, and the raw rows the
 * findings were derived from, so support does not have to ask for screenshots.
 */
import { receiverOutcomeLabel } from "./troubleshootingApi";
import {
  AlertsReceived,
  CameraLastAlarms,
  SiteCameraTestRun,
  CameraLiveness,
  ConnectionError,
  Finding,
  GapResult,
  SettingsCompare,
  SiteChanges,
  TroubleshootingSite,
  WireGuardTunnel,
} from "../troubleshootingInterfaces";
import { formatRelative } from "./formatters";
import { clampNotice, markerLabel, windowPhrase } from "./changesWindow";
import { getPeerHealth, PEER_HEALTH_LABEL } from "./wireGuardHealth";
import {
  connectionErrorAdvice,
  lastWorkingServerId,
} from "./connectionErrorAdvice";

export interface ReportInput {
  scopeLabel: string;
  findings: Finding[];
  tunnels: WireGuardTunnel[];
  sites: TroubleshootingSite[];
  getServerName: (serverId: number | null) => string;
  lastCheckedAt: number | null;
  sampleData: boolean;
  alertsReceived?: AlertsReceived | null;
  cameraLastAlarms?: CameraLastAlarms | null;
  /** The latest per-camera test alarm run for the site (ENG-2633). */
  siteCameraTest?: SiteCameraTestRun | null;
  siteChanges?: SiteChanges | null;
  /** "Compare to when it last worked", when it was run (ENG-2244). */
  siteChangesCompare?: SettingsCompare | null;
  /** One site: the per-camera "working now" read, or why it is missing. */
  cameraLiveness?: GapResult<CameraLiveness> | null;
  /** The all-clear card as shown, so an unconfirmed verdict is copied as such. */
  allClear?: Finding | null;
  nowMs?: number;
}

/** Above this many peers in scope, only the unhealthy ones are listed. */
export const MAX_LISTED_PEERS = 25;

export const buildDiagnosticReport = (input: ReportInput): string => {
  const nowMs = input.nowMs ?? Date.now();
  const siteName = (id: number) =>
    input.sites.find((s) => s.id === id)?.name || `site ${id}`;
  const lines: string[] = [];
  lines.push("Actuate troubleshooting report");
  lines.push(`Generated: ${new Date(nowMs).toISOString()}`);
  lines.push(`Scope: ${input.scopeLabel}`);
  if (input.lastCheckedAt)
    lines.push(
      `Checks last run: ${new Date(input.lastCheckedAt).toISOString()}`
    );
  if (input.sampleData)
    lines.push(
      "NOTE: sample data preview was on; rows marked Sample are illustrative."
    );
  lines.push("");

  if (input.alertsReceived) {
    const alerts = input.alertsReceived;
    lines.push(
      `Alerts received (last ${alerts.windowHours} h, since ${alerts.since}): ${
        alerts.count
      }${alerts.truncated ? "+ (capped at the API page limit)" : ""}` +
        (alerts.count
          ? ` across ${alerts.camerasAlerted} camera${
              alerts.camerasAlerted === 1 ? "" : "s"
            }`
          : "")
    );
    if (alerts.latest)
      lines.push(
        `  Latest: ${alerts.latest.at} from ${alerts.latest.siteName} · ${alerts.latest.cameraName}`
      );
    lines.push("");
  }

  if (input.cameraLastAlarms) {
    const cams = input.cameraLastAlarms;
    lines.push(
      `Cameras at ${siteName(cams.siteId)} — last alarm (${
        cams.windowHours
      } h, since ${cams.since}${
        cams.truncated
          ? ", the API's maximum page; earlier alerts not read"
          : ""
      }): ${cams.quiet} of ${cams.rows.length} quiet`
    );
    cams.rows.forEach((row) =>
      lines.push(
        `  ${row.cameraName} | last alarm ${row.lastAt ?? "NONE in window"} | ${
          row.count
        } alert${row.count === 1 ? "" : "s"}`
      )
    );
    lines.push("");
  }

  if (input.siteCameraTest) {
    const run = input.siteCameraTest;
    lines.push(
      `Test alarms at ${siteName(run.siteId)} (${
        run.mode === "quiet" ? "quiet cameras" : "all cameras"
      }, sent ${run.sentAt}): ${run.rows.length} sent${
        run.skipped.length ? `, ${run.skipped.length} skipped` : ""
      }`
    );
    run.rows.forEach((row) =>
      lines.push(
        `  ${row.cameraName} | ${
          row.result === "pending"
            ? "no answer yet"
            : receiverOutcomeLabel(row.result, row.detail)
        }${row.result === "failed" && row.detail ? ` (${row.detail})` : ""}`
      )
    );
    run.skipped.forEach((row) =>
      lines.push(`  ${row.cameraName} | skipped: ${row.reason}`)
    );
    lines.push(
      "  Accepted by receiver = the monitoring station's receiver took the event; it does not confirm an operator saw it."
    );
    lines.push("");
  }

  if (input.cameraLiveness) {
    const result = input.cameraLiveness;
    if (result.status !== "ok") {
      lines.push(
        `Connector heartbeat and camera video (working now): UNAVAILABLE (${
          result.status === "unavailable"
            ? `${result.endpoint} is not deployed in this environment yet`
            : `${result.endpoint} failed: ${result.message}`
        })`
      );
    } else {
      const live = result.data;
      const c = live.connector;
      lines.push(
        `Connector heartbeat and camera video (working now, read ${
          live.checkedAt ?? "unknown"
        })`
      );
      lines.push(
        c
          ? `  Connector: last reported ${
              c.lastSeenAt
                ? `${c.lastSeenAt} (${formatRelative(c.lastSeenAt, nowMs)})`
                : "never"
            }; census running: ${
              c.censusRunning == null
                ? "unknown"
                : c.censusRunning
                ? "yes"
                : "NO"
            }; in schedule: ${
              c.inSchedule == null ? "unknown" : c.inSchedule ? "yes" : "no"
            }`
          : "  Connector: this site has no connector heartbeat"
      );
      // VMS login failures with the VMS's own answer (ENG-2252).
      const lastWorking = lastWorkingServerId(
        input.siteChangesCompare?.siteId === live.siteId
          ? input.siteChangesCompare
          : null
      );
      const loginLine = (error: ConnectionError) => {
        const advice = connectionErrorAdvice(error, {
          lastWorkingServerId: lastWorking,
          nowMs,
        });
        return `code ${advice.raw.code}; detail ${JSON.stringify(
          advice.raw.detail
        )}${
          advice.raw.detail.trim() ? "" : " (empty response)"
        }; failing since ${
          advice.raw.firstFailedAt
            ? `${advice.raw.firstFailedAt} (${advice.raw.firstFailedRelative})`
            : "unknown"
        }; latest at ${
          advice.raw.at
            ? `${advice.raw.at} (${advice.raw.atRelative})`
            : "unknown"
        }; integration ${error.integration ?? "unknown"}; scope ${
          error.scope
        } — ${advice.title}. ${advice.action}`;
      };
      // Absent on an admin without ENG-2252, so silence is not "healthy".
      if (live.connectionError)
        lines.push(
          `  VMS login (site): FAILING, ${loginLine(live.connectionError)}`
        );
      live.cameras.forEach((camera) => {
        const video = camera.lastFrameAt ?? camera.bandwidthUpdatedAt;
        lines.push(
          `  ${camera.cameraName} | ${
            camera.continuous ? "continuous" : "motion or event only"
          } | last video ${video ?? "none recorded"}${
            live.signals.lastImage
              ? ` | last image ${camera.lastImageReceivedAt ?? "none recorded"}`
              : ""
          }${camera.brokenFrame ? " | BROKEN FRAMES" : ""}${
            camera.lastConnectionError
              ? ` | LOGIN FAILING: ${loginLine(camera.lastConnectionError)}`
              : ""
          }`
        );
      });
      live.errors.forEach((error) =>
        lines.push(`  Could not read ${error.source}: ${error.detail}`)
      );
    }
    lines.push("");
  }

  if (input.siteChanges) {
    const changes = input.siteChanges;
    const total = changes.settingsVersions.length + changes.adminChanges.length;
    lines.push(
      `Our changes to ${siteName(changes.siteId)} (${windowPhrase(
        changes,
        nowMs
      )}, since ${changes.since}): ${
        changes.settingsAvailable
          ? total === 0
            ? "none"
            : `${changes.settingsVersions.length} settings version${
                changes.settingsVersions.length === 1 ? "" : "s"
              } saved, ${changes.adminChanges.length} admin edit${
                changes.adminChanges.length === 1 ? "" : "s"
              }`
          : "settings history UNAVAILABLE (bucket could not be listed)"
      }`
    );
    const clamp = clampNotice(changes, nowMs);
    if (clamp) lines.push(`  ${clamp}`);
    if (changes.truncated)
      lines.push("  Showing the newest 100 saves; older saves not listed.");
    const markers = changes.markers;
    if (markers) {
      lines.push(
        `  ${markerLabel("lastAlert", null)}: ${
          markers.lastAlertAt
            ? `${markers.lastAlertAt} (${formatRelative(
                markers.lastAlertAt,
                nowMs
              )})`
            : "none on record"
        }`
      );
      lines.push(
        `  ${markerLabel("lastConnected", markers.lastConnectedSource)}: ${
          markers.lastConnectedAt
            ? `${markers.lastConnectedAt} (${formatRelative(
                markers.lastConnectedAt,
                nowMs
              )})`
            : "none on record"
        }`
      );
    }
    changes.settingsVersions.forEach((version) => {
      lines.push(
        `  ${version.savedAt} settings saved${
          version.savedBy ? ` by ${version.savedBy}` : ""
        } (version ${version.versionId}${version.isLatest ? ", latest" : ""}${
          version.readable ? "" : ", unreadable"
        })`
      );
      version.changes.forEach((change) =>
        lines.push(
          `    ${change.path}: ${JSON.stringify(
            change.from
          )} -> ${JSON.stringify(change.to)}`
        )
      );
    });
    changes.adminChanges.forEach((change) =>
      lines.push(
        `  ${change.at} ${change.field}: ${JSON.stringify(
          change.from
        )} -> ${JSON.stringify(change.to)}${
          change.user ? ` (${change.user})` : ""
        }`
      )
    );
    const c = changes.connector;
    lines.push(
      `  Connector: booted ${c.podBootTime ?? "unknown"} on settings ${
        c.settingsVersionId ?? "unknown"
      }; latest settings ${c.latestSettingsVersionId ?? "unknown"} saved ${
        c.latestSettingsSavedAt ?? "unknown"
      }; running latest: ${
        c.runningLatest == null ? "unknown" : c.runningLatest ? "yes" : "NO"
      }`
    );
    lines.push("");
  }

  if (input.siteChangesCompare) {
    const compare = input.siteChangesCompare;
    lines.push(
      `Settings now vs when it last worked (${compare.at}, ${formatRelative(
        compare.at,
        nowMs
      )}):`
    );
    if (compare.sources.length === 0) lines.push("  no settings to compare");
    compare.sources.forEach((source) => {
      const label =
        source.scheduleName ||
        (source.flexScheduleId != null
          ? `flex schedule ${source.flexScheduleId}`
          : "site settings");
      lines.push(
        `  ${label} [${source.settingsKey}]: ${
          source.identical
            ? "IDENTICAL (probably not our configuration)"
            : `${source.changes.length} difference${
                source.changes.length === 1 ? "" : "s"
              }`
        } (baseline ${source.baseline?.versionId ?? "none"}, current ${
          source.current?.versionId ?? "none"
        })`
      );
      source.changes.forEach((change) =>
        lines.push(
          `    ${change.path}: ${JSON.stringify(
            change.from
          )} -> ${JSON.stringify(change.to)}`
        )
      );
      if (source.reason) lines.push(`    Note: ${source.reason}`);
    });
    lines.push("");
  }

  lines.push(`Findings (${input.findings.length})`);
  if (input.findings.length === 0) {
    if (input.allClear) {
      lines.push(
        `  None. [${
          input.allClear.severity === "good" ? "OK" : "UNCONFIRMED"
        }] ${input.allClear.title}`
      );
      lines.push(`  ${input.allClear.body}`);
    } else {
      lines.push("  None. Every check we can run from our side is passing.");
    }
  }
  input.findings.forEach((finding, index) => {
    lines.push(
      `  ${index + 1}. [${finding.severity.toUpperCase()}] ${finding.title}`
    );
    // ISO where the finding has it: the on-screen date is in the viewer's
    // locale and timezone, which support cannot read back (QA DG-35).
    const since = finding.sinceIso || finding.since;
    if (since) lines.push(`     Since: ${since}`);
    lines.push(`     Owner: ${finding.owner}`);
    lines.push(`     ${finding.body}`);
    if (finding.detailLine) lines.push(`     Detail: ${finding.detailLine}`);
    finding.steps.forEach((step, stepIndex) =>
      lines.push(`     ${stepIndex + 1}) ${step}`)
    );
    if (finding.affectedSiteIds.length) {
      lines.push(
        `     Affected sites: ${finding.affectedSiteIds
          .map(siteName)
          .join(", ")}`
      );
    }
  });
  lines.push("");

  // Only the tunnels in the chosen scope (the caller narrows them). A large
  // group still lists every unhealthy peer, but summarises the healthy ones
  // so the ticket stays readable.
  const healthy = input.tunnels.filter(
    (tunnel) => getPeerHealth(tunnel, nowMs) === "healthy"
  );
  const listed =
    input.tunnels.length > MAX_LISTED_PEERS
      ? input.tunnels.filter(
          (tunnel) => getPeerHealth(tunnel, nowMs) !== "healthy"
        )
      : input.tunnels;
  lines.push(
    input.tunnels.length === 0
      ? "WireGuard peers: none serve this scope"
      : `WireGuard peers (${input.tunnels.length})`
  );
  if (listed.length < input.tunnels.length)
    lines.push(
      `  ${healthy.length} healthy peers not listed; the ${listed.length} below need attention.`
    );
  listed.forEach((tunnel) => {
    const health = getPeerHealth(tunnel, nowMs);
    lines.push(
      `  ${tunnel.name} | server ${input.getServerName(
        tunnel.server
      )} | tunnel IP ${tunnel.client_tunnel_ip} | subnets ${
        (tunnel.subnets || []).join(" ") || "-"
      } | last handshake ${formatRelative(tunnel.last_handshake, nowMs)} | ${
        PEER_HEALTH_LABEL[health]
      }`
    );
  });
  return lines.join("\n");
};

export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
};
